import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-License-Identifier: AGPL-3.0-or-later
import { useEffect, useMemo, useState } from 'react';
import { computeDraftCalculations, hasCalculatedItems, isCalculatedItem, isHiddenFromPatient, loadedFhirPathEngine, loadFhirPathEngine, } from '@intakeform/validation';
import { Stack } from '../primitives/Stack.js';
import { LanguageProvider, useLanguage } from '../i18n/LanguageProvider.js';
import { translateField } from '../i18n/translate.js';
import {} from '../i18n/languages.js';
import { createDefaultRegistry } from './builtin.js';
import { isItemActive } from './enableWhen.js';
import { TouchedProvider } from './FieldHost.js';
import { FieldTypeRegistry } from './registry.js';
function findingsForLinkId(findings, linkId) {
    if (findings === undefined || findings.length === 0)
        return [];
    return findings.filter((f) => f.linkId === linkId);
    // Form-level findings (linkId omitted) are intentionally dropped here —
    // the renderer surfaces them via a separate slot owned by the caller.
}
function ItemNode({ item, answers, findings, disabled, registry, onChange, calculated, hideFromPatient, }) {
    const { lang } = useLanguage();
    if (!isItemActive(item, answers))
        return null;
    if (hideFromPatient === true && isHiddenFromPatient(item))
        return null;
    // Group items contain no value themselves — recurse into children.
    if (item.type === 'group') {
        const legend = item.text !== undefined ? translateField(item, 'text', lang, item.text) : undefined;
        return (_jsxs("fieldset", { "data-group": "true", "data-link-id": item.linkId, children: [legend !== undefined ? _jsx("legend", { children: legend }) : null, _jsx(Stack, { gap: 12, children: (item.item ?? []).map((child) => (_jsx(ItemNode, { item: child, answers: answers, findings: findings, disabled: disabled, registry: registry, onChange: onChange, calculated: calculated, hideFromPatient: hideFromPatient }, child.linkId))) })] }));
    }
    // Display items are read-only blocks of text.
    if (item.type === 'display') {
        return (_jsx("p", { "data-display": "true", "data-link-id": item.linkId, children: item.text !== undefined ? translateField(item, 'text', lang, item.text) : null }));
    }
    const registration = registry.resolve(item);
    if (registration === undefined) {
        // Surface unknown types loudly in dev rather than silently swallowing
        // them — a missing registration is almost always a build / wiring bug.
        return (_jsx("div", { "data-unknown-field": "true", "data-link-id": item.linkId, "data-item-type": item.type, children: `No renderer registered for item.type "${item.type}" (linkId=${item.linkId}).` }));
    }
    // A calculated item shows the value its sources imply and is never typed
    // into; a `readOnly` item is shown but not editable either.
    const isCalculated = isCalculatedItem(item);
    const readOnly = isCalculated || item.readOnly === true;
    const Component = registration.component;
    return (_jsx(Component, { linkId: item.linkId, item: item, value: isCalculated ? calculated.get(item.linkId) : answers[item.linkId], errors: findingsForLinkId(findings, item.linkId), disabled: disabled === true || readOnly, onChange: (next) => {
            if (readOnly)
                return;
            onChange(item.linkId, next);
        } }));
}
/**
 * Top-level Questionnaire renderer. Walks `questionnaire.item` recursively,
 * evaluates `enableWhen` against `answers`, and mounts the component the
 * `registry` returns for each active item. State is owned by the caller —
 * the renderer is a pure function of `(questionnaire, answers, findings)`.
 */
export function Renderer(props) {
    const { questionnaire, answers, onChange, findings, disabled, registry, onTouched, language, visibleRootLinkIds, hideFromPatient, } = props;
    const effectiveRegistry = useMemo(() => registry ?? createDefaultRegistry(), [registry]);
    const lang = language ?? 'en';
    // fhirpath.js is fetched only for a form that calculates something; until
    // it arrives, calculated items stay blank (the API recomputes on submit).
    const needsEngine = useMemo(() => hasCalculatedItems(questionnaire), [questionnaire]);
    const [engine, setEngine] = useState(() => loadedFhirPathEngine() ?? null);
    useEffect(() => {
        if (!needsEngine || engine !== null)
            return;
        let live = true;
        loadFhirPathEngine().then((loaded) => {
            if (live)
                setEngine(() => loaded);
        }, () => {
            // Scores stay blank; the submit still scores server-side.
        });
        return () => {
            live = false;
        };
    }, [needsEngine, engine]);
    const calculated = useMemo(() => computeDraftCalculations(questionnaire, answers, engine), [questionnaire, answers, engine]);
    const rootItems = useMemo(() => {
        const all = questionnaire.item ?? [];
        if (visibleRootLinkIds === undefined)
            return all;
        const allow = new Set(visibleRootLinkIds);
        return all.filter((item) => allow.has(item.linkId));
    }, [questionnaire.item, visibleRootLinkIds]);
    return (_jsx(LanguageProvider, { lang: lang, children: _jsx(TouchedProvider, { value: onTouched, children: _jsx("form", { "data-questionnaire-id": questionnaire.id ?? questionnaire.name ?? '', noValidate: true, children: _jsx(Stack, { gap: 16, children: rootItems.map((item) => (_jsx(ItemNode, { item: item, answers: answers, findings: findings, disabled: disabled, registry: effectiveRegistry, onChange: onChange, calculated: calculated, hideFromPatient: hideFromPatient }, item.linkId))) }) }) }) }));
}
//# sourceMappingURL=Renderer.js.map